import { preconnect } from 'react-dom'

import {
  groupRows,
  SidebarNavGroup,
  SidebarPitches,
  SidebarSwitchRow,
  useProductShell,
} from '@/components/sidebar'
import { BOOKKEEPER_ORIGIN, bookkeeperHref } from '@/config/config'
import { SidebarTab } from '@/lib/sidebarTab'
import type { SidebarNav, User } from '@/models/user'
import { ConsoleNav } from './ConsoleNav'
import { CONSOLE_SWITCH, emptyTabPitches } from './navigation'

interface SidebarTabContentProps {
  tab: SidebarTab
  user: User
  pathname: string
  onSwitch: (tab: SidebarTab) => void
}

const TAB_ROWS: Record<Exclude<SidebarTab, SidebarTab.Console>, keyof SidebarNav> = {
  [SidebarTab.Insurance]: 'insurance',
  [SidebarTab.Compliance]: 'vault',
  [SidebarTab.Finance]: 'finance',
}

export function SidebarTabContent({ tab, user, pathname, onSwitch }: SidebarTabContentProps) {
  const shell = useProductShell()
  if (tab === SidebarTab.Console) return <ConsoleNav pathname={pathname} onSwitch={onSwitch} />

  if (tab === SidebarTab.Finance) {
    // Opening the tab is the intent signal; handshake now so a click pays for the document alone.
    preconnect(BOOKKEEPER_ORIGIN)
  }

  const rows = user.sidebar[TAB_ROWS[tab]]
  // Bookkeeper sends paths on its own origin.
  const groups = groupRows(
    tab === SidebarTab.Finance ? rows.map((row) => ({ ...row, url: bookkeeperHref(row.url) })) : rows
  )

  return (
    <>
      {/* Nothing to list means the account does not have the product, so it gets pitched instead. */}
      {rows.length === 0 && <SidebarPitches pitches={emptyTabPitches(tab, user)} />}
      {groups.map((group, index) => (
        <SidebarNavGroup
          key={group.label ?? `group-${index}`}
          label={group.label}
          links={group.links}
          separator={index > 0}
        />
      ))}
      {/* Outside the rows: staff with no current org get an empty Insurance tab, and this
          is their only way back to the console. */}
      {tab === SidebarTab.Insurance && user.isCaiAdmin && !shell && (
        <SidebarNavGroup separator={rows.length > 0}>
          <SidebarSwitchRow
            title={CONSOLE_SWITCH.title}
            icon={CONSOLE_SWITCH.icon}
            onClick={() => onSwitch(CONSOLE_SWITCH.tab)}
          />
        </SidebarNavGroup>
      )}
    </>
  )
}
